import { Formik } from "formik";
import {
    StyledLink,
    StyledReturnHomeLink,
    LoginLogo,
    SignUpEmailComponent,
    SignUpNameComponent,
    SignUpPasswordComponent,
} from "@components";
import { ISignUpEmail, ISignUpName, ISignUpPassword } from "@interfaces";
import { signupEmailSchema, signupNameSchema, signupPasswordSchema } from "@validations";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch, toggleLoading } from "@redux";
import { authApi } from "@api";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { handleLogin } from "src/Utilities/routeHelper";
import { PATH_LOGIN, PATH_HOME } from "@routes";
import { markSignupPendingUpgrade } from "src/Module/Checkout/pointsAgentUpgrade";
import {
    isSignupEmailError,
    signupErrorMessage,
    signupPasswordError,
} from "src/Utilities/signupErrors";

export const SignUp = () => {
    const EMAIL_SCREEN = "email-screen";
    const NAME_SCREEN = "name-screen";
    const PASSWORD_SCREEN = "password-screen";
    const [screen, setScreen] = useState(EMAIL_SCREEN);
    const dispatch = useDispatch<AppDispatch>();
    const initialValuesEmail: ISignUpEmail = {
        email: "",
    };
    const [email, setEMail] = useState(initialValuesEmail.email);
    const initialValuesName: ISignUpName = {
        first_name: "",
        last_name: "",
    };
    const [name, setName] = useState(initialValuesName);
    const location = useLocation();
    const navigate = useNavigate();
    const initialValuesPassword: ISignUpPassword = {
        password: "",
        ReEnterPassword: "",
    };

    return (
        <>
            <LoginLogo
                src="/images/logos/Primary_logo_color.svg"
                onClick={() => navigate(PATH_HOME)}
            />
            {screen === EMAIL_SCREEN ? (
                <Formik
                    initialValues={initialValuesEmail}
                    validationSchema={signupEmailSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setScreen(NAME_SCREEN);
                        setSubmitting(false);
                        setEMail(values.email);
                    }}
                >
                    {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => {
                        return (
                            <div style={{ width: "100%" }}>
                                <SignUpEmailComponent
                                    values={values}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    handleSubmit={handleSubmit}
                                    isSubmitting={isSubmitting}
                                />
                            </div>
                        );
                    }}
                </Formik>
            ) : screen === NAME_SCREEN ? (
                <Formik
                    initialValues={name}
                    validationSchema={signupNameSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setName({
                            first_name: values.first_name.trim(),
                            last_name: values.last_name.trim(),
                        });
                        setSubmitting(false);
                        setScreen(PASSWORD_SCREEN);
                    }}
                >
                    {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                        <div style={{ width: "100%" }}>
                            <SignUpNameComponent
                                values={values}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    )}
                </Formik>
            ) : (
                <Formik
                    initialValues={initialValuesPassword}
                    validationSchema={signupPasswordSchema}
                    onSubmit={async (values, { setErrors }) => {
                        const signUpData = {
                            first_name: name.first_name,
                            last_name: name.last_name,
                            email: email,
                            password: values.password,
                            ReEnterPassword: values.ReEnterPassword,
                        };
                        try {
                            dispatch(toggleLoading(true));
                            const res = await authApi.signUp(signUpData);
                            if (res.data.code === 200) {
                                // Upgrading has to wait for the emailed code: the
                                // account is unverified here and this screen is left
                                // behind the moment login succeeds, so the flag is
                                // what carries the intent to the verification screen.
                                markSignupPendingUpgrade(signUpData.email);
                                await handleLogin(
                                    dispatch,
                                    { email: signUpData.email, password: signUpData.password },
                                    navigate,
                                    location,
                                    (errorMessage) => {
                                        console.error(errorMessage);
                                        navigate(PATH_LOGIN);
                                    },
                                );
                                return;
                            }
                        } catch (error: any) {
                            console.error("Error during signup:", {
                                status: error.response?.status,
                                data: error.response?.data,
                                message: error.message,
                            });
                            setErrors({ password: signupPasswordError(error) });
                            ErrorToast({ Message: signupErrorMessage(error) });
                            if (isSignupEmailError(error)) {
                                setScreen(EMAIL_SCREEN);
                            }
                        } finally {
                            dispatch(toggleLoading(false));
                        }
                    }}
                >
                    {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                        <div style={{ width: "100%" }}>
                            <SignUpPasswordComponent
                                values={values}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    )}
                </Formik>
            )}
            <StyledReturnHomeLink>
                <Link to={PATH_HOME}>Return to home</Link>
            </StyledReturnHomeLink>
            <StyledLink>
                Already have an account? <NavLink to={"/login" + location.search}>Sign in </NavLink>
                with your account
            </StyledLink>
        </>
    );
};
